/**
 * The booth log file: its categories, and the contract for the renderer writing
 * into it (src/main/utils/logManager.ts).
 *
 * Why it exists: the main-process log is the only booth log that reaches the
 * cloud (logUploader.ts sends it to R2), but most of what a camera does happens
 * in the renderer, whose console never left the machine. A report like "the
 * Nikon often shows this screen" could not be checked against anything.
 *
 * Every line carries one category from a closed list, right after the level:
 *
 *   [2026-10-17T07:12:03.411Z] [WARN] [camera] (renderer) connect.fail: no answer {"attempt":2}
 *   [2026-10-17T07:12:04.002Z] [INFO] [network] [APP] Internet check: online via https://...
 *
 * so a reader (a person in System Logs, or an AI given one day's file) can keep
 * one category and still read it in order with everything else on the machine.
 * The `[timestamp] [LEVEL] ` head is unchanged: the duplicate-launch check, the
 * System Logs page and the log sweep scripts all parse it.
 *
 * Forward- and backward-compatible by construction: an older shell has no
 * `logs.write`, so a renderer must feature-detect it and drop its lines there;
 * an older renderer never calls it.
 */

export const BOOTH_LOG_WRITE_CHANNEL = 'logs:write'

export const LOG_CATEGORIES = [
  'camera',
  'print',
  'payment',
  'license',
  'upload',
  'sync',
  'session',
  'network',
  'auth',
  'update',
  'storage',
  'system',
  'ui',
  'other'
] as const

export type LogCategory = (typeof LOG_CATEGORIES)[number]

export const LOG_LEVELS = ['debug', 'info', 'warn', 'error'] as const

export type LogLevel = (typeof LOG_LEVELS)[number]

/** One line the renderer asks main to write. Everything but `message` is optional. */
export interface BoothLogEntryInput {
  /** When it happened, epoch ms. Main uses its own clock when this is missing or far off. */
  at?: number
  level?: LogLevel
  /** Inferred from the message's `[PREFIX]` when missing. */
  category?: LogCategory
  /** A stable dotted key, e.g. `connect.fail`, so lines of one kind can be counted. */
  event?: string
  message: string
  /** Written as one line of JSON after the message. */
  data?: Record<string, unknown>
}

export interface BoothLogWriteResult {
  written: number
  /** Over the per-call or per-minute cap, or not a valid entry. */
  dropped: number
}

/** A renderer that has more than this should send it in several calls. */
export const MAX_ENTRIES_PER_WRITE = 200
